import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

const Breadcrumbs = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const pathSegments = location?.pathname?.split('/')?.filter(Boolean);
  
  const getBreadcrumbItems = () => {
    const items = [{ label: 'Dashboard', path: '/transaction-dashboard', icon: 'Home' }];
    
    if (pathSegments?.length === 0) {
      return items;
    }

    const routeMap = {
      'transaction-dashboard': { label: 'Dashboard', icon: 'LayoutDashboard' },
      'transaction-details': { label: 'Transaction Details', icon: 'FileText' },
      'document-management': { label: 'Document Management', icon: 'FolderOpen' },
      'digital-signature': { label: 'Digital Signature', icon: 'PenTool' },
      'stakeholder-management': { label: 'Stakeholder Management', icon: 'Users' },
      'reports-analytics': { label: 'Reports & Analytics', icon: 'BarChart3' }
    };

    let currentPath = '';
    pathSegments?.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      if (routeMap?.[segment]) {
        items?.push({
          label: routeMap?.[segment]?.label,
          path: currentPath,
          icon: routeMap?.[segment]?.icon,
          isLast: index === pathSegments?.length - 1
        });
      } else if (segment?.match(/^[0-9a-f-]+$/)) {
        // Handle dynamic IDs (transaction IDs, document IDs, etc.)
        const parentSegment = pathSegments?.[index - 1];
        if (parentSegment === 'transaction-details') {
          items?.push({
            label: `Transaction #${segment?.slice(0, 8)}`,
            path: currentPath,
            icon: 'Hash',
            isLast: index === pathSegments?.length - 1
          });
        } else if (parentSegment === 'digital-signature') {
          items?.push({
            label: `Document #${segment?.slice(0, 8)}`,
            path: currentPath,
            icon: 'FileSignature',
            isLast: index === pathSegments?.length - 1
          });
        }
      }
    });

    return items;
  };

  const breadcrumbItems = getBreadcrumbItems();

  const handleNavigation = (path) => {
    navigate(path);
  };

  if (breadcrumbItems?.length <= 1) {
    return null;
  }

  return (
    <nav className="sticky top-16 z-40 bg-background/95 backdrop-blur-sm border-b border-border px-6 py-3">
      <div className="flex items-center space-x-2 text-sm">
        {breadcrumbItems?.map((item, index) => (
          <React.Fragment key={item?.path}>
            {index > 0 && (
              <Icon 
                name="ChevronRight" 
                size={14} 
                className="text-muted-foreground flex-shrink-0" 
              />
            )}
            
            <div className="flex items-center space-x-1.5 min-w-0">
              {item?.icon && (
                <Icon 
                  name={item?.icon} 
                  size={14} 
                  className={`flex-shrink-0 ${
                    item?.isLast 
                      ? 'text-primary' :'text-muted-foreground'
                  }`}
                />
              )}
              
              {item?.isLast ? (
                <span className="font-medium text-primary truncate">
                  {item?.label}
                </span>
              ) : (
                <button
                  onClick={() => handleNavigation(item?.path)}
                  className="text-muted-foreground hover:text-foreground transition-smooth truncate"
                >
                  {item?.label}
                </button>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
      {/* Blockchain Context Indicator */}
      {location?.pathname?.includes('digital-signature') && (
        <div className="mt-2 flex items-center space-x-2 text-xs text-muted-foreground">
          <Icon name="Shield" size={12} className="text-success" />
          <span>Blockchain verified</span>
          <span className="text-muted-foreground">•</span>
          <span className="font-mono">Last updated: {new Date()?.toLocaleTimeString()}</span>
        </div>
      )}
      {/* Transaction Status Indicator */}
      {location?.pathname?.includes('transaction-details') && (
        <div className="mt-2 flex items-center space-x-4 text-xs">
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-warning rounded-full animate-pulse" />
            <span className="text-warning font-medium">In Progress</span>
          </div>
          <span className="text-muted-foreground">•</span>
          <span className="text-muted-foreground">3 of 5 signatures collected</span>
          <span className="text-muted-foreground">•</span>
          <span className="text-muted-foreground font-mono">TXN-2025-001</span>
        </div>
      )}
    </nav>
  );
};

export default Breadcrumbs;